import React, { useEffect, useMemo, useState } from 'react';
import {
  DataGrid,
  GridColDef,
  CellTypes,
  DataGridProps,
  P,
} from '@fashionnovateam/nova-ui-toolkit';
import moment from 'moment/moment';
import { find } from 'lodash';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Button from '../ReusableComponents/Button/Button';

type InviteRow = {
  id: number;
  company_name: CellTypes.DataGridCellCompanyParams;
  status: CellTypes.DataGridCellBadgeParams;
  email: CellTypes.DataGridCellEmailParams;
  token: CellTypes.DataGridCellStringParams;
  invited_by: CellTypes.DataGridCellPersonParams;
  expires_at: CellTypes.DataGridCellDateTimeParams;
  refresh: CellTypes.DataGridCellButtonParams;
};

const columns: GridColDef[] = [
  {
    field: 'company_name',
    headerName: 'Influencer Name',
    render: 'company',
    flex: 1,
    sortable: true,
    minWidth: 150,
    disableColumnMenu: true,
  },
  {
    field: 'status',
    headerName: 'Status',
    render: 'badge',
    flex: 1,
    sortable: true,
    minWidth: 100,
    disableColumnMenu: true,
  },
  {
    field: 'email',
    headerName: 'Email',
    render: 'email',
    flex: 1,
    sortable: true,
    minWidth: 100,
    disableColumnMenu: true,
  },
  {
    field: 'token',
    headerName: 'Token',
    render: 'string',
    flex: 1,
    sortable: false,
    display: 'flex',
    minWidth: 100,
    disableColumnMenu: true,
  },
  {
    field: 'person',
    headerName: 'Invited By',
    render: 'person',
    flex: 1,
    sortable: false,
    display: 'flex',
    minWidth: 100,
    disableColumnMenu: true,
  },
  {
    field: 'expires_at',
    headerName: 'Expires in',
    render: 'dateTime',
    flex: 1,
    sortable: true,
    display: 'flex',
    minWidth: 100,
    disableColumnMenu: true,
  },
  {
    field: 'refresh',
    headerName: 'Actions',
    render: 'button',
    flex: 1,
    sortable: false,
    display: 'flex',
    minWidth: 100,
    disableColumnMenu: true,
  },
];

type Props = {
  rows: InviteToken[];
  count: number;
  team: any[];
  loading: boolean;
  refresh: (tokenId: number) => { inviteToken: InviteToken };
  params: { sortBy: string; sortOrder: 'ASC' | 'DESC'; page: number };
  hasPermissionToRefresh: boolean;
} & Omit<DataGridProps<InviteRow>, 'columns'>;

export interface InviteToken {
  company_name: string;
  company_type: string;
  createdAt: string;
  custom_payment_terms: string | null;
  deleted_at: string | null;
  email: string;
  expires_at: string;
  id: number;
  originated_by: string | null;
  payment_term: string | null;
  reason: string;
  requesting_party: string;
  requesting_party_id: string | null;
  status: string;
  token: string;
  updatedAt: string;
  vendor_id: string | null;
  vendor_type: string;
}

function InviteTokensDataGrid({
  rows,
  count,
  team,
  refresh,
  loading,
  params,
  hasPermissionToRefresh,
  ...datagridProps
}: Props) {
  const [displayedRows, setRows] = useState(rows);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentToken, setCurrentToken] = useState<InviteToken | null>(null);

  useEffect(() => {
    setRows(rows);
  }, [rows]);

  const getTokenStatus = (
    token: InviteToken,
  ): {
    label: string;
    color: 'green' | 'primary' | 'red';
  } => {
    if (token.status === 'TOKEN_REGISTERED') {
      return { label: 'Registered', color: 'green' };
    }
    if (moment(token.expires_at).isBefore(moment())) {
      return { label: 'Expired', color: 'red' };
    }
    return { label: 'Email sent', color: 'primary' };
  };

  const getInvitedBy = (token: InviteToken) => {
    const invitedBy = find(team, (t) => t.id === token.requesting_party_id);

    return invitedBy ? `${invitedBy.first_name} ${invitedBy.last_name}` : '';
  };

  const transformedRows = useMemo(
    () =>
      displayedRows.map((token) => {
        const { color, label } = getTokenStatus(token);
        return {
          id: token.id,
          company_name: { name: token.company_name },
          status: {
            children: label,
            color,
            contrast: 'low',
          },
          email: token.email,
          token: token.token,
          invited_by: {
            name: getInvitedBy(token),
          },
          expires_at: {
            activity: true,
            time: true,
            date: token.expires_at,
          },
          refresh: {
            size: 'sm',
            color: 'secondary',
            children: 'Refresh',
            disabled: !hasPermissionToRefresh,
            onClick: async () => {
              const { inviteToken } = await refresh(token.id);
              setRows((prevRows) =>
                prevRows.map((row) =>
                  row.id === token.id ? inviteToken : row,
                ),
              );
              setCurrentToken(inviteToken);
              setDialogOpen(true);
            },
          },
        } satisfies InviteRow;
      }),
    [displayedRows],
  );

  return (
    <>
      <DataGrid
        hideFilterButton
        hideGroupButton
        columns={columns}
        loading={loading}
        rows={transformedRows}
        initialState={{
          pinnedColumns: {
            left: ['influencer_name'],
          },
        }}
        sortingMode="server"
        paginationMode="server"
        rowCount={count || 0}
        {...datagridProps}
      />
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogContent style={{ minHeight: '150px' }}>
          <Grid container justifyContent="center" style={{ padding: '25px' }}>
            <P>
              The token for <b>{currentToken?.company_name}</b> has been
              refreshed and an email has been sent to{' '}
              <b>{currentToken?.email}</b>.
            </P>
          </Grid>
          <Grid container justifyContent="center">
            <Button
              small
              variant="outlined"
              color="primary"
              onClick={() => setDialogOpen(false)}
            >
              Close
            </Button>
          </Grid>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default InviteTokensDataGrid;
